<script setup lang="ts">
    import { computed, ComputedRef, ref, watch } from 'vue';
    import { http } from '@/shared/httpWrapper';
    import useBuefy from '@/shared/composables/useBuefy';
    import useShow from '@/shared/composables/useShow';
    import _ from 'lodash';
    import { useShippingStore } from '@/stores/shippingStore';
    import { packageMetadata } from '../shippingPackageMetadata';
    import {
        createShippingPackage,
        getLibraryList,
        LibraryListItem,
        ShippingPackage,
        updateShippingPackage
    } from '@/shared/helpers/api';
    import { LibraryItemFilter, ShippingWizard } from './wizard';
    import { ExtractionSpecification } from './extractionSpecification';
    import { newLibraryContentWizard } from './contentSpecification';
    import { newLibraryDefinitionWizard } from './librarySpecification';
    import LibraryWizard from './LibraryWizard.vue';
    import ContentWizard from './ContentWizard.vue';
    import LibrarySelectorModal from './LibrarySelectorModal.vue';
    import SaveEditedPackageModal from './SaveEditedPackageModal.vue';

    const store = useShippingStore();
    const buefy = useBuefy();
    const { showError } = useShow();

    const libraries = ref<LibraryListItem[]>([]);
    const usedLibraries = ref<LibraryListItem[]>([]);
    const showLibrarySelector = ref(false);
    const showSaveModal = ref(false);
    const droppedItems = ref<string[]>([]);
    const wizard = ref<ShippingWizard<ExtractionSpecification> | null>(null);
    const elements = ref<ExtractionSpecification[]>([]);
    let editGeneration = 0;

    const usedLibraryNames: ComputedRef<string[]> = computed(() =>
        usedLibraries.value.map(library => library.name));

    const editingPackageName: ComputedRef<string> = computed(() =>
        store.editingPackage?.name || store.editingPackage?.packageId || '');

    watch(() => store.editingPackage, async editingPackage => {
        if (editingPackage)
            await startEditing(editingPackage);
    }, { immediate: true });

    async function startEditing(shippingPackage: ShippingPackage): Promise<void> {
        // Incremented by every new edit and by closeWizard, so that a load that was overtaken is discarded
        const generation = ++editGeneration;
        const isCurrent = () => generation === editGeneration;

        // Each load gets its own element list, so overlapping loads can't add to each other's setup
        elements.value = [];
        droppedItems.value = [];
        usedLibraries.value = [];

        const loading = buefy.loading.open({});
        try {
            let started: StartedWizard;
            if (shippingPackage.packageType === 'LibraryDefinitionsPackage') {
                started = await startLibraryDefinitionsWizard();
            } else if (shippingPackage.packageType === 'LibraryContentPackage') {
                started = await startLibraryContentWizard();
            } else {
                throw new Error(`Packages of type ${shippingPackage.packageType} can't be edited`);
            }

            const { startedWizard, availableLibraries } = started;
            const dropped = await startedWizard.loadSaved(
                JSON.parse(shippingPackage.extractionOptions),
                availableLibraries);

            if (!isCurrent())
                return;

            libraries.value = availableLibraries;
            droppedItems.value = dropped;
            const libraryByName = _.keyBy(availableLibraries, 'name');
            usedLibraries.value = startedWizard.elements.map(element => libraryByName[element.libraryName]);
        } catch (err) {
            if (!isCurrent())
                return;

            showError(err);
            closeWizard();
        } finally {
            loading.close();
        }
    }

    async function createPackage(name: string, remarks: string): Promise<void> {
        if (!wizard.value)
            return;

        const loading = buefy.loading.open({});
        try {
            const { packageType, description, options } = wizard.value.finish();
            const result = await createShippingPackage(http, packageType, name, description, remarks, options);
            await selectPackageAndClose(result.data.packageId);
        } catch (err) {
            showError(err);
        } finally {
            loading.close();
        }
    }

    async function finishCreateShippingPackage(): Promise<void> {
        const name = ''; // PackageId will be displayed as Name, but can easily be changed
        const remarks = '';
        await createPackage(name, remarks);
    }

    async function saveEditedAsNewPackage(name: string): Promise<void> {
        showSaveModal.value = false;
        await createPackage(name, store.editingPackage?.remarks || '');
    }

    async function overwriteEditedPackage(): Promise<void> {
        showSaveModal.value = false;
        if (!wizard.value || !store.editingPackage)
            return;

        const loading = buefy.loading.open({});
        try {
            const packageId = store.editingPackage.packageId;
            const { description, options } = wizard.value.finish();
            await updateShippingPackage(http, packageId, description, options);
            await selectPackageAndClose(packageId);
        } catch (err) {
            showError(err);
        } finally {
            loading.close();
        }
    }

    async function selectPackageAndClose(packageId: string): Promise<void> {
        closeWizard();
        await store.refresh();
        const selected = store.packages.find(x => x.packageId === packageId);
        if (selected) {
            await store.setSelectedPackage(selected);
        }
    }

    async function loadLibraries(libraryFilter: LibraryItemFilter): Promise<LibraryListItem[]> {
        const result = await getLibraryList(http);
        libraries.value = _.sortBy(result.filter(libraryFilter), 'name');
        return libraries.value;
    }

    type StartedWizard = {
        startedWizard: ShippingWizard<ExtractionSpecification>;
        availableLibraries: LibraryListItem[];
    };

    async function startLibraryDefinitionsWizard(): Promise<StartedWizard> {
        const startedWizard = newLibraryDefinitionWizard(elements.value);
        wizard.value = startedWizard;
        const availableLibraries = await loadLibraries(_ => true);
        return { startedWizard, availableLibraries };
    }

    async function startLibraryContentWizard(): Promise<StartedWizard> {
        const startedWizard = newLibraryContentWizard(elements.value);
        wizard.value = startedWizard;
        const availableLibraries = await loadLibraries(library => !library.isVirtual);
        return { startedWizard, availableLibraries };
    }

    /*
        The selector returns the full set of libraries that should end up in the package.
        Libraries that were already selected are left untouched so that any per-library
        configuration (content filters, code sets) survives editing the selection.
    */
    async function applyLibrarySelection(selection: LibraryListItem[]): Promise<void> {
        showLibrarySelector.value = false;
        if (!wizard.value)
            return;

        const selectedNames = new Set(selection.map(library => library.name));
        const toRemove = usedLibraries.value.filter(library => !selectedNames.has(library.name));
        const toAdd = selection.filter(library => !usedLibraryNames.value.includes(library.name));

        if (toRemove.length === 0 && toAdd.length === 0)
            return;

        const loading = buefy.loading.open({});
        try {
            toRemove.forEach(library => removeLibrary(library.name));
            for (const library of toAdd) {
                await wizard.value.addLibrary(library);
                usedLibraries.value.push(library);
            }
        } catch (err) {
            showError(err);
        } finally {
            loading.close();
        }
    }

    function removeLibrary(libraryName: string): void {
        if (wizard.value) {
            wizard.value.removeLibrary(libraryName);
            const index = usedLibraries.value.findIndex(x => x.name === libraryName);
            if (index >= 0)
                usedLibraries.value.splice(index, 1);
        }
    }

    function moveLibrary(libraryName: string, offset: number): void {
        if (!wizard.value)
            return;

        wizard.value.moveLibrary(libraryName, offset);
        const order = wizard.value.elements.map(element => element.libraryName);
        usedLibraries.value = _.sortBy(usedLibraries.value, library => order.indexOf(library.name));
    }

    function closeWizard(): void {
        editGeneration++;
        store.closeExportWizard();
        showLibrarySelector.value = false;
        showSaveModal.value = false;
        droppedItems.value = [];
        wizard.value = null;
        elements.value = [];
        usedLibraries.value = [];
        libraries.value = [];
    }

    // Future improvement: Consider disabling create button unless input makes sense
    // (at least one library, at least one codeset pr lib, no half-added filter input)

</script>

<template>
    <template v-if="store.showExportWizard">
        <template v-if="!wizard && !store.editingPackage">
            <div class="columns">
                <div class="column">
                    <h2 class="title is-4 has-text-grey">
                        Select type of export to create..
                    </h2>
                </div>
                <div class="column is-flex is-justify-content-end is-align-items-end">
                    <b-button
                        icon-left="close"
                        @click="closeWizard">
                        Close
                    </b-button>
                </div>
            </div>

            <div
                class="shipping-wizard-menu-item is-flex is-flex-direction-row is-size-5 is-clickable mb-2"
                title="Create package.."
                @click="startLibraryDefinitionsWizard">
                <b-icon
                    class="mr-6 ml-4"
                    size="is-large"
                    :icon="packageMetadata.type.icon('LibraryDefinitionsPackage')" />
                <div>
                    <b>Library definitions</b><br>
                    This option exports library definitions and related data like subscriptions, library group membership, etc.
                </div>
            </div>

            <div
                class="shipping-wizard-menu-item is-flex is-flex-direction-row is-size-5 is-clickable mb-2"
                title="Create package.."
                @click="startLibraryContentWizard">
                <b-icon
                    class="mr-6 ml-4"
                    size="is-large"
                    :icon="packageMetadata.type.icon('LibraryContentPackage')" />
                <div>
                    <b>Library content</b><br>
                    This option exports data from one or mye libraries that you select. Filters can be applied.
                </div>
            </div>
        </template>

        <template v-if="wizard">
            <div class="columns">
                <div class="column">
                    <h2
                        v-if="store.editingPackage"
                        class="title is-4 has-text-grey">
                        Edit package {{ editingPackageName }}..
                    </h2>
                    <h2
                        v-else
                        class="title is-4 has-text-grey">
                        Select libraries to export..
                    </h2>
                </div>
                <div class="column is-flex is-justify-content-end is-align-items-end">
                    <div
                        v-if="store.editingPackage"
                        class="buttons">
                        <b-button
                            type="is-primary"
                            icon-left="floppy"
                            :disabled="elements.length === 0"
                            @click="showSaveModal = true">
                            Save changes..
                        </b-button>
                        <b-button
                            icon-left="close-circle"
                            @click="closeWizard">
                            Cancel
                        </b-button>
                    </div>
                    <b-button
                        v-else
                        icon-left="close"
                        @click="closeWizard">
                        Close
                    </b-button>
                </div>
            </div>

            <b-message
                v-if="droppedItems.length > 0"
                type="is-warning"
                title="Parts of the saved setup could not be restored">
                The libraries have changed since this package was created. These items were removed from the setup:
                <ul class="mt-2">
                    <li
                        v-for="item in droppedItems"
                        :key="item">
                        {{ item }}
                    </li>
                </ul>
            </b-message>

            <p
                v-if="wizard.type === 'LibraryContent'"
                class="block has-text-grey">
                On import, libraries that are referenced by other libraries (CodeRef) are imported first,
                based on the library definitions in the target environment, so import the library definitions
                before the content. The order below is only used where that can't decide: for libraries that
                reference each other in a cycle, and libraries that depend on them.
            </p>

            <div class="mb-5">
                <b-field grouped>
                    <p class="control">
                        <b-button
                            type="is-info"
                            icon-left="plus"
                            :disabled="libraries.length === 0"
                            @click="showLibrarySelector = true">
                            Select libraries
                        </b-button>
                    </p>
                    <p class="control is-flex is-align-items-center has-text-grey">
                        {{ usedLibraries.length }} selected
                    </p>
                </b-field>
            </div>

            <library-selector-modal
                v-if="showLibrarySelector"
                :libraries="libraries"
                :selected="usedLibraryNames"
                @confirm="applyLibrarySelection"
                @cancel="showLibrarySelector = false" />
        </template>

        <library-wizard
            :wizard="wizard"
            @remove-library-click="removeLibrary" />

        <content-wizard
            :wizard="wizard"
            @remove-library-click="removeLibrary"
            @move-library-click="moveLibrary" />

        <template v-if="wizard && !store.editingPackage">
            <div
                class="is-flex is-justify-content-center pt-5 pb-5">
                <b-button
                    type="is-primary"
                    size="is-large"
                    outlined
                    :disabled="elements.length === 0"
                    @click="finishCreateShippingPackage">
                    Create package with these options
                </b-button>
            </div>
        </template>

        <save-edited-package-modal
            v-if="showSaveModal"
            :package-name="editingPackageName"
            @save-as-new="saveEditedAsNewPackage"
            @overwrite="overwriteEditedPackage"
            @cancel="showSaveModal = false" />
    </template>
</template>

<style>
.shipping-wizard-menu-item {
    border-top: solid 1px #999999;
    border-bottom: solid 1px #999999;
    padding: 1em;
    background-color: #eeeeee;
}
.shipping-library-options-box {
    border-top: solid 1px #999999;
    padding: 1em;
}
.shipping-library-options-container {
    border-bottom: solid 1px #999999;
}
</style>
